/* eslint-disable max-len */
import { Entity, SystemInterface } from '@goodboydigital/odie';

import { Entity25D } from '../../display/odie25d/Entity25D';
import { DepthOrder, Game } from '../Game';
import { BashEffectEntity } from './BashEffectEntity';
import { BoostEffectEntity } from './BoostEffectEntity';
import { CrashEffectEntity } from './CrashEffectEntity';

interface Effect extends Entity25D
{
    follow?: Entity25D;
    followOffsetX?: number;
    followOffsetY?: number;
    step(delta: number): boolean;
    reset(): void;
}

interface EffectParams
{
    x: number,
    y: number,
    r?: number
    z?: number
    follow?: Entity25D
}

/**
 * Manage tweens that should be tied to game's delta time
 */
export class EffectsSystem implements SystemInterface
{
    public game: Game;
    public effects: Effect[];

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.effects = [];
    }

    public postrender(): void
    {
        if (this.game.paused) return;
        const delta = this.game.time.frameTime;
        let i = this.effects.length;

        while (i--)
        {
            const effect = this.effects[i];
            const finished = effect.step(delta);

            if (effect.follow)
            {
                effect.x = effect.follow.x + (effect.followOffsetX ?? 0);
                effect.y = effect.follow.y + (effect.followOffsetY ?? 0);
                effect.rotation = effect.follow.rotation;
            }

            if (finished) this.removeEffect(effect, i);
        }
    }

    public playBash(params: EffectParams): Effect
    {
        return this.addEffect(BashEffectEntity, params);
    }

    public playCrash(params: EffectParams): Effect
    {
        return this.addEffect(CrashEffectEntity, params);
    }

    public playBoost(params: EffectParams): Effect
    {
        return this.addEffect(BoostEffectEntity, params);
    }

    private addEffect(ctor: new () => Effect, params: EffectParams): Effect
    {
        const effect = this.game.pool.get(ctor) as Effect;

        this.game.addToScene(effect);

        effect.follow = params.follow;
        effect.followOffsetX = params.x;
        effect.followOffsetY = params.y;
        effect.x = params.x;
        effect.y = params.y;
        effect.z = params.z ?? DepthOrder.EFFECT;
        effect.rotation = params.r ?? 0;
        effect.view25d.render();
        effect.view.updateTransform();
        effect.reset();

        this.effects.push(effect);

        return effect;
    }

    private removeEffect(effect: Effect, index?: number)
    {
        if (index === undefined) index = this.effects.indexOf(effect);
        this.game.removeFromScene(effect);
        this.game.pool.return(effect);
        this.effects.splice(index, 1);
    }
}
